import React, { useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * ShareButtons Component
 * 
 * Provides social media sharing buttons for Twitter/X, Facebook, LinkedIn, and copy link.
 * 
 * @param {Object} props
 * @param {string} props.url - The URL to share (defaults to current page)
 * @param {string} props.title - The title/text to share
 * @param {string} props.description - Optional description for platforms that support it
 * @param {string} props.variant - "icons" for icon-only, "full" for icons with text
 * @param {string} props.className - Additional CSS classes
 */
export const ShareButtons = ({ 
  url = typeof window !== 'undefined' ? window.location.href : 'https://dtrcreative.com',
  title = "Check out DTR Creative - AI-Powered Book Writing Platform",
  description = "Transform your ideas into professionally written books with AI.",
  variant = "icons",
  className = ""
}) => {
  const [copied, setCopied] = useState(false);
  
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };
  
  const handleShare = (platform) => {
    const link = shareLinks[platform];
    if (platform === 'email') {
      window.location.href = link;
    } else {
      window.open(link, '_blank', 'width=600,height=400,noopener,noreferrer');
    }
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  // Native share (mobile devices)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      handleCopyLink();
    }
  };
  
  const iconClass = "w-5 h-5";
  
  // SVG Icons
  const TwitterIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
  
  const FacebookIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
  
  const LinkedInIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
  
  const RedditIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
    </svg>
  );
  
  const EmailIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
  
  const CopyIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
  
  const CheckIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
  
  const ShareIcon = () => (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="18" cy="5" r="3"/>
      <circle cx="6" cy="12" r="3"/>
      <circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );
  
  if (variant === "minimal") {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleNativeShare}
        className={className}
        data-testid="share-button"
      >
        <ShareIcon />
        <span className="ml-2">Share</span>
      </Button>
    );
  }
  
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="share-buttons">
      {/* Twitter/X */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('twitter')}
        className="hover:bg-black hover:text-white transition-colors"
        title="Share on X (Twitter)"
        data-testid="share-twitter"
      >
        <TwitterIcon />
      </Button>
      
      {/* Facebook */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('facebook')}
        className="hover:bg-blue-600 hover:text-white transition-colors"
        title="Share on Facebook"
        data-testid="share-facebook"
      >
        <FacebookIcon />
      </Button>
      
      {/* LinkedIn */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('linkedin')}
        className="hover:bg-blue-700 hover:text-white transition-colors"
        title="Share on LinkedIn"
        data-testid="share-linkedin"
      >
        <LinkedInIcon />
      </Button>
      
      {/* Reddit */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('reddit')}
        className="hover:bg-orange-600 hover:text-white transition-colors"
        title="Share on Reddit"
        data-testid="share-reddit"
      >
        <RedditIcon />
      </Button>
      
      {/* Email */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => handleShare('email')}
        className="hover:bg-gray-600 hover:text-white transition-colors"
        title="Share via Email"
        data-testid="share-email"
      >
        <EmailIcon />
      </Button>
      
      {/* Copy Link */}
      <Button
        variant="outline"
        size="icon"
        onClick={handleCopyLink}
        className={`transition-colors ${copied ? 'bg-green-500 text-white' : 'hover:bg-gray-100'}`}
        title={copied ? "Copied!" : "Copy Link"}
        data-testid="share-copy"
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </Button>
    </div>
  );
};

/**
 * ShareBookButton - Specialized share button for completed books
 */
export const ShareBookButton = ({ bookTitle, bookId }) => {
  const shareText = `I just created "${bookTitle}" using DTR Creative's AI Book Writing Platform! 📚✨`;
  const shareUrl = `https://dtrcreative.com/books/${bookId}`;
  
  return (
    <ShareButtons
      url={shareUrl}
      title={shareText}
      description="Transform your ideas into professionally written books with AI."
      variant="icons"
    />
  );
};

export default ShareButtons;
